// @flow
import * as React from 'react';
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import points14Messages from 'esm-web-pro/messages/points14Messages';
import { injectIntl, IntlShape } from 'react-intl';
import OpticalPortSplicedFiberRow
    from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/OpticalPortSplicedFiberRow';
import OpticalPortSplicedPortRow
    from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/OpticalPortSplicedPortRow';
import MeatballsIcon from 'esm-web-pro/components/layout/sidePanel/icons/MeatballsIcon';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import { withRouter } from 'react-router-dom';
import type { RouterHistory, Match } from 'react-router-dom';
import type { VisualisedFiber } from 'esm-web-pro/containers/pages/network/objects/cable/FiberList';
import { highlightFiberCircuit } from 'esm-web-pro/containers/pages/network/highlight';
import { connectAPI } from 'speed-js-react';
import { fiberCircuitCallback } from 'esm-web-pro/containers/pages/network/objects/cable/CableFiber';
import useHighlightFiber from 'esm-web-pro/containers/pages/network/objects/fiber/useHighlightFiber';
import { withLeaflet } from 'react-leaflet';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { POINT12_CONTENT_TOOLTIP_KEY }
    from 'esm-web-pro/containers/pages/network/objects/point12/CableList/Point12CableList';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';

type Props = {
    opticalPort: Object,
    setClickedRowIdentity: Function,
    clickedRowIdentity: string,
    intl: IntlShape,
    getFiberCableSeq: Function,
    onConnect: Function,
    history: RouterHistory,
    match: Match,
    onView: Function,
    map: Object,
    onDisconnect: Function,
    pointCoords: [number, number],
    point: Object,
    cables: Object,

    getFiberCircuitAndPoints: Function,
    leaflet: LeafletContext,
    switchPortIndex: Number,
    disablePortDisconnect: boolean,
    portLoading?: boolean,
    fiberLoading?: boolean,
}

const OpticalPortRow = (props: Props) => {
    const {
        clickedRowIdentity, setClickedRowIdentity, opticalPort, intl: { formatMessage },
        getFiberCableSeq, onConnect, history, match, map: networkMap, onDisconnect, pointCoords,
        leaflet: { map }, getFiberCircuitAndPoints, onView, point, cables, switchPortIndex,
        disablePortDisconnect, portLoading, fiberLoading,
    } = props;

    const [highlightFiber] = useHighlightFiber();

    const rowId = `PORT-${opticalPort.id}`;

    const { fiber, port } = opticalPort;

    const actions = [
        {
            key: `${opticalPort.uuid}-more`,
            icon: <MeatballsIcon />,
            size: '20px',
            width: '3px',
            callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                const path = match.url.split('/layer');
                history.push(`${path[0]}/layer/${opticalPort.type}/${opticalPort.identity}`);
            },
            tooltip: formatMessage(actionMessages.more),
            tooltipKey: POINT12_CONTENT_TOOLTIP_KEY,
        },
    ];

    const handleConnectToFiber = () => {
        onConnect({ portToFiber: opticalPort });
    };

    const handleConnectToPort = () => {
        onConnect({ portToPort: opticalPort });
    };

    const handleDisconnectFiber = () => {
        onDisconnect({ fiberFromPort: opticalPort });
    };

    const handleDisconnectPort = () => {
        onDisconnect({ portFromPort: opticalPort });
    };

    const getTitle = (currentPort) => {
        if (currentPort.type === 'opticalodfport') {
            return `${formatMessage(points14Messages.opticalport)} ${currentPort.sequence}`;
        }

        return null;
    };

    const handleClick = React.useCallback((fiberUuid) => {
        getFiberCircuitAndPoints(fiberUuid, networkMap).then((vFiber: VisualisedFiber) => {
            const [callbacks, circuitPath] = highlightFiberCircuit(
                vFiber.path, vFiber, map, highlightFiber, pointCoords,
            );
            map.highlightedGeometry = {
                ...map.highlightedGeometry, bounds: circuitPath,
            };
            onView(null, callbacks, circuitPath);
        });
    }, [getFiberCircuitAndPoints, map, networkMap, pointCoords, onView, highlightFiber]);

    const onPortClick = React.useCallback(() => {
        if (opticalPort.type === 'opticalswitchport') {
            setClickedRowIdentity(`${rowId}-port`);
            if (port && port.fiber) {
                handleClick(port.fiber.uuid);
            } else {
                onView();
            }
        }
    }, [handleClick, opticalPort, setClickedRowIdentity, port, onView, rowId]);

    const rowProps = {};
    if (!port && !fiber) {
        rowProps.hoverIndicator = null;
    } else {
        rowProps.onClick = () => {
            if (fiber) {
                handleClick(fiber.uuid);
            } else if (port && port.fiber) {
                handleClick(port.fiber.uuid);
            }

            setClickedRowIdentity(rowId);
        };
    }

    return (
        <>
            {(opticalPort.type !== 'opticalswitchport') && (
                <ContentRow
                    padLevel={1}
                    rowId={rowId}
                    clickedRowIdentity={clickedRowIdentity}
                    actions={(
                        <RowActions
                            iconPadding="3px"
                            gapBetweenActions="small"
                            actions={actions}
                        />
                    )}
                    {...rowProps}
                >
                    <ValueText>
                        {getTitle(opticalPort)}
                    </ValueText>
                </ContentRow>
            )}
            { opticalPort.type === 'opticalodfport' && (
                <OpticalPortSplicedFiberRow
                    getFiberCableSeq={getFiberCableSeq}
                    fiber={fiber}
                    parentRowId={rowId}
                    onConnect={handleConnectToFiber}
                    onDisconnect={handleDisconnectFiber}
                    map={networkMap}
                    cables={cables}
                    loading={fiberLoading}
                    padLevel={2}
                />
            )}
            <OpticalPortSplicedPortRow
                getFiberCableSeq={getFiberCableSeq}
                port={port}
                parentRowId={rowId}
                onConnect={handleConnectToPort}
                onDisconnect={handleDisconnectPort}
                map={networkMap}
                point={point}
                switchPortIndex={switchPortIndex}
                onClick={opticalPort.type === 'opticalswitchport' && opticalPort.port ? onPortClick : null}
                clickedRowIdentity={clickedRowIdentity}
                disablePortDisconnect={disablePortDisconnect}
                loading={portLoading}
                padLevel={2}
            />
        </>
    );
};

OpticalPortRow.defaultProps = {
    portLoading: false,
    fiberLoading: false,
};

export default withLeaflet(withRouter(injectIntl(connectAPI(fiberCircuitCallback)(OpticalPortRow))));
